import React, { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Package,
  AlertCircle,
  CheckCircle2,
  Minus,
  Plus,
  Infinity,
  Layers,
} from "lucide-react";

const QuantitySection = React.memo(
  ({
    onQuantityChange,
    initialQuantity,
    onValidationChange,
    /** When true, total is derived from size stocks — input is read-only. */
    lockedFromSizes = false,
  }) => {
    const [quantity, setQuantity] = useState(
      initialQuantity === null ||
        initialQuantity === undefined ||
        initialQuantity === ""
        ? null
        : Number(initialQuantity)
    );
    const [isUnlimited, setIsUnlimited] = useState(
      !lockedFromSizes &&
        (initialQuantity === null ||
          initialQuantity === undefined ||
          initialQuantity === "")
    );
    const [quantityError, setQuantityError] = useState("");

    React.useEffect(() => {
      if (!lockedFromSizes) return;
      const num =
        initialQuantity === null ||
        initialQuantity === undefined ||
        initialQuantity === ""
          ? 0
          : Number(initialQuantity);
      setIsUnlimited(false);
      setQuantity(Number.isFinite(num) ? num : 0);
      setQuantityError("");
      onValidationChange?.(true);
    }, [lockedFromSizes, initialQuantity, onValidationChange]);

    const formatQuantity = (value) => {
      if (!value || value === "") return "";
      const cleaned = String(value).replace(/[^0-9]/g, "");
      return cleaned ? parseInt(cleaned, 10).toString() : "";
    };

    const validateQuantity = useCallback(
      (qty) => {
        const numQty = Number(qty);

        if (qty === "" || qty === null || qty === undefined) {
          setQuantityError("");
          onValidationChange?.(true);
          return true;
        }

        if (numQty < 0) {
          setQuantityError("Quantity cannot be negative");
          onValidationChange?.(false);
          return false;
        }

        if (!Number.isInteger(numQty)) {
          setQuantityError("Quantity must be a whole number");
          onValidationChange?.(false);
          return false;
        }

        if (numQty > 1000000) {
          setQuantityError("Quantity cannot exceed 1,000,000");
          onValidationChange?.(false);
          return false;
        }

        setQuantityError("");
        onValidationChange?.(true);
        return true;
      },
      [onValidationChange]
    );

    const handleQuantityChange = useCallback(
      (value) => {
        if (isUnlimited) setIsUnlimited(false);
        const formatted = formatQuantity(value);
        const numValue = formatted === "" ? null : Number(formatted);
        setQuantity(numValue);
        onQuantityChange(numValue === null ? null : formatted);
        validateQuantity(formatted);
      },
      [onQuantityChange, validateQuantity, isUnlimited]
    );

    const handleUnlimitedToggle = useCallback(() => {
      const next = !isUnlimited;
      setIsUnlimited(next);
      if (next) {
        setQuantity(null);
        onQuantityChange(null);
        setQuantityError("");
        onValidationChange?.(true);
      } else {
        setQuantity(0);
        onQuantityChange("0");
        validateQuantity("0");
      }
    }, [isUnlimited, onQuantityChange, onValidationChange, validateQuantity]);

    const adjustQuantity = (adjustment) => {
      if (isUnlimited) {
        setIsUnlimited(false);
        setQuantity(0);
        onQuantityChange("0");
        return;
      }
      const currentQty = quantity === null ? 0 : Number(quantity);
      const newQty = Math.max(0, currentQty + adjustment);
      setQuantity(newQty);
      onQuantityChange(newQty.toString());
      validateQuantity(newQty.toString());
    };

    React.useEffect(() => {
      if (!isUnlimited && quantity !== null) {
        validateQuantity(quantity?.toString() || "0");
      }
    }, [initialQuantity, validateQuantity, isUnlimited, quantity]);

    const displayQty =
      isUnlimited || quantity === null ? null : Number(quantity) || 0;
    const stockHint =
      lockedFromSizes
        ? "Sum of size stocks"
        : isUnlimited || quantity === null
          ? "Unlimited"
          : displayQty === 0
            ? "Out of stock"
            : displayQty < 10
              ? "Low stock"
              : "In stock";

    const presets = [10, 25, 50, 100, 250];

    return (
      <motion.div
        className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
          <div className="flex gap-2.5 items-start min-w-0">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#FF6C6C]/10">
              <Package className="w-4 h-4 text-[#FF6C6C]" />
            </div>
            <div className="min-w-0">
              <h4 className="text-sm font-semibold text-gray-900">
                Quantity Available
              </h4>
              <p className="mt-0.5 text-xs text-gray-500 leading-relaxed">
                {lockedFromSizes
                  ? "Locked — total is the sum of stock per size"
                  : "How many units are available for this deal"}
              </p>
            </div>
          </div>

          {!lockedFromSizes && (
            <button
              type="button"
              onClick={handleUnlimitedToggle}
              className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-all ${
                isUnlimited
                  ? "bg-[#FF6C6C] text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              <Infinity className="w-3.5 h-3.5" />
              {isUnlimited ? "Unlimited" : "Set unlimited"}
            </button>
          )}
        </div>

        {lockedFromSizes && (
          <div className="mb-3 flex items-center gap-2 rounded-lg border border-[#FF6C6C]/15 bg-[#FF6C6C]/5 px-3 py-2">
            <Layers className="w-3.5 h-3.5 text-[#FF6C6C] shrink-0" />
            <p className="text-[11px] text-gray-600">
              Edit units on each size above to change this total.
            </p>
          </div>
        )}

        {!isUnlimited && (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div
              className={`flex items-center overflow-hidden rounded-lg border ${
                quantityError
                  ? "border-red-300"
                  : lockedFromSizes
                    ? "border-[#FF6C6C]/30 bg-[#FF6C6C]/5"
                    : "border-gray-200 bg-white"
              }`}
            >
              {!lockedFromSizes && (
                <button
                  type="button"
                  onClick={() => adjustQuantity(-1)}
                  disabled={displayQty == null || displayQty <= 0}
                  className="flex h-10 w-10 items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-30"
                  aria-label="Decrease quantity"
                >
                  <Minus className="w-4 h-4" />
                </button>
              )}
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                readOnly={lockedFromSizes}
                value={
                  quantity === null || quantity === undefined ? "" : quantity
                }
                onChange={(e) => handleQuantityChange(e.target.value)}
                placeholder="0"
                className={`h-10 min-w-[4.5rem] flex-1 bg-transparent text-center text-base font-bold tabular-nums text-gray-900 outline-none ${
                  lockedFromSizes ? "px-4 cursor-default" : "px-1"
                }`}
              />
              {!lockedFromSizes && (
                <button
                  type="button"
                  onClick={() => adjustQuantity(1)}
                  className="flex h-10 w-10 items-center justify-center text-gray-500 hover:bg-gray-50"
                  aria-label="Increase quantity"
                >
                  <Plus className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                  lockedFromSizes || (displayQty != null && displayQty > 0)
                    ? "bg-emerald-50 text-emerald-700"
                    : displayQty === 0
                      ? "bg-red-50 text-red-600"
                      : "bg-gray-100 text-gray-600"
                }`}
              >
                {(lockedFromSizes || (displayQty != null && displayQty > 0)) && (
                  <CheckCircle2 className="w-3 h-3" />
                )}
                {stockHint}
                {displayQty != null && (
                  <span className="tabular-nums">
                    · {displayQty.toLocaleString()}
                  </span>
                )}
              </span>

              {!lockedFromSizes && (
                <div className="flex flex-wrap gap-1">
                  {presets.map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => handleQuantityChange(String(preset))}
                      className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-600 hover:border-[#FF6C6C]/40 hover:text-[#FF6C6C]"
                    >
                      {preset}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {isUnlimited && (
          <div className="flex items-center gap-2 rounded-lg border border-[#FF6C6C]/20 bg-[#FF6C6C]/5 px-3 py-2.5">
            <Infinity className="w-4 h-4 text-[#FF6C6C]" />
            <p className="text-xs font-medium text-gray-700">
              Unlimited availability — quantity tracking is off for this deal.
            </p>
          </div>
        )}

        <AnimatePresence>
          {quantityError && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              className="mt-2 flex items-center gap-1.5 text-xs font-medium text-red-600"
            >
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              {quantityError}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }
);

QuantitySection.displayName = "QuantitySection";

export default QuantitySection;
